import { useId, useState } from 'react'
import { IconCategoryGrid } from '../../../components/icons'
import { OTHER_GROUP_NAME } from '../catalogTaxonomy'
import type { CategoryGroup } from '../groupCategories'
import type { CatalogCategory } from '../types'
import { CategoryTileImage } from './CategoryTileImage'

export interface ShopByCategoryProps {
  readonly groups: readonly CategoryGroup[]
  /** Real categories not covered by any group — rendered as a distinctly
   * labeled trailing "Other" group rather than dropped (UID-253). Pass `[]`
   * to omit it entirely. */
  readonly other: readonly string[]
  /** Enriched category details (`imageUrl`/`seriesCount`) keyed by category
   * name, built by `CatalogBrowsePage` from `useCategoryTaxonomy`'s ready
   * state. A missing entry degrades to the icon glyph, no count line. */
  readonly categoryDetails: ReadonlyMap<string, CatalogCategory>
  /**
   * UID-253 interim behavior: the `listing`/category-results screen this
   * chip would otherwise navigate to (UID-251/252/34 territory) isn't built
   * yet. Rather than fabricate a destination or silently do nothing, the
   * container is expected to surface an honest "not built yet" toast — see
   * `CatalogBrowsePage.tsx`'s `handleCategoryClick`. This component itself
   * stays presentation-only and has no opinion on what the click does.
   */
  readonly onCategoryClick: (category: string) => void
}

const TINT_CLASSES = [
  'catalog-group-tint-0',
  'catalog-group-tint-1',
  'catalog-group-tint-2',
  'catalog-group-tint-3',
] as const

function categoryCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'category' : 'categories'}`
}

function seriesCountLabel(count: number): string {
  return `${count} series`
}

/**
 * Count-based overflow heuristic (not JS-measured): with `grid-auto-rows:
 * 1fr` removed (see `catalog.css`'s `.catalog-group-grid`), each card's
 * `.catalog-group-chips` list is independently capped at
 * `--catalog-group-chips-max-height` (~3 chip rows at typical 2-column card
 * widths). A `ResizeObserver`/`scrollHeight` measurement would be more
 * precise, but jsdom never lays out flex-wrap, so a measured check is
 * untestable here and would silently never fire in CI. A fixed count
 * threshold is deterministic in both the browser and tests. 9 chips is a
 * conservative estimate of "3 rows' worth" for this card's typical width —
 * groups at or under it always fit within the collapsed cap without
 * clipping; groups over it get the toggle. If the design's column widths
 * change materially, re-tune this constant alongside
 * `--catalog-group-chips-max-height`.
 */
const COLLAPSED_CHIP_THRESHOLD = 9

function GroupCard({
  name,
  categories,
  tintClass,
  categoryDetails,
  onCategoryClick,
}: {
  readonly name: string
  readonly categories: readonly string[]
  readonly tintClass: string
  readonly categoryDetails: ReadonlyMap<string, CatalogCategory>
  readonly onCategoryClick: (category: string) => void
}) {
  const [expanded, setExpanded] = useState(false)
  const panelId = useId()
  const overflows = categories.length > COLLAPSED_CHIP_THRESHOLD

  return (
    <div className="catalog-group-card">
      <div className="catalog-group-header">
        <span className={`catalog-group-icon ${tintClass}`}>
          <IconCategoryGrid />
        </span>
        <div>
          <h2 className="catalog-group-name">{name}</h2>
          <p className="catalog-group-subcount">{categoryCountLabel(categories.length)}</p>
        </div>
      </div>
      <div
        id={panelId}
        className={`catalog-group-chips${
          overflows && !expanded ? ' catalog-group-chips-collapsed' : ''
        }`}
      >
        {categories.map((category) => {
          const details = categoryDetails.get(category)
          return (
            <button
              key={category}
              type="button"
              className="catalog-group-chip"
              onClick={() => onCategoryClick(category)}
            >
              <CategoryTileImage
                imageUrl={details?.imageUrl ?? null}
                tintClass={tintClass}
                className="catalog-group-chip-thumb"
              />
              <span>{category}</span>
              {details?.seriesCount != null && (
                <span className="catalog-group-chip-count">
                  {seriesCountLabel(details.seriesCount)}
                </span>
              )}
            </button>
          )
        })}
      </div>
      {overflows && (
        <button
          type="button"
          className="catalog-group-chips-toggle"
          aria-expanded={expanded}
          aria-controls={panelId}
          onClick={() => setExpanded((current) => !current)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  )
}

/**
 * The "Shop by category" section of the catalog browse landing (UID-253
 * rebuild) — see `Fictiv Platform.dc.html` ~line 863 for the design source
 * of truth. A 2-column grid of group cards; each card lists its leaf
 * categories as clickable chips. Presentational only: `groups`/`other` come
 * from `useCategoryTaxonomy` (`groupCategories.ts`), and a chip click is
 * fully delegated to `onCategoryClick`.
 *
 * Leaf-chip thumbnails: each chip renders the real, BFF-derived `imageUrl`
 * from `categoryDetails` (a category's own series image — see
 * `category-catalog-enrichment.ts`) via `CategoryTileImage`, falling back to
 * the same tinted icon glyph the group header uses when no real image is
 * available — never fabricated (ADR-0090/0091 "omit, don't stub").
 */
export function ShopByCategory({
  groups,
  other,
  categoryDetails,
  onCategoryClick,
}: ShopByCategoryProps) {
  return (
    <section aria-labelledby="shop-by-category-heading" className="catalog-shop-by-category">
      <h1 id="shop-by-category-heading" className="catalog-shop-by-category-heading">
        Shop by category
      </h1>
      <div className="catalog-group-grid">
        {groups.map((group, index) => (
          <GroupCard
            key={group.name}
            name={group.name}
            categories={group.categories}
            tintClass={TINT_CLASSES[index % TINT_CLASSES.length] ?? TINT_CLASSES[0]}
            categoryDetails={categoryDetails}
            onCategoryClick={onCategoryClick}
          />
        ))}
        {other.length > 0 && (
          <GroupCard
            name={OTHER_GROUP_NAME}
            categories={other}
            tintClass="catalog-group-tint-other"
            categoryDetails={categoryDetails}
            onCategoryClick={onCategoryClick}
          />
        )}
      </div>
    </section>
  )
}
